import React, { useState, useMemo } from 'react';
import { Formik, Form, Field } from 'formik';
import { User, UserInput } from '@core/generated/gql-types';
import { userActions } from '@core/user/user.action';
import { useWorkerStore } from '@core/worker/useWorkerStore';
import EditableSection from '@/components/shared/EditableSection';
import {
  actionFieldInputBlockClassName,
  actionPrimaryButtonClassName,
} from '@core/theme/actionTheme.util';
import { formatLabel, getCustomAttribute } from '@/utils/utils';
import {
  EXPLICIT_CUSTOM_ATTRIBUTE_REFS,
  getExplicitCustomAttributeLabel,
  HIDDEN_CUSTOM_ATTRIBUTE_REFS,
} from '@/modules/workers/utils/customAttributes.utils';
import { RESIDENCY_STATUS_OPTIONS } from '@/modules/workers/utils/workEligibility.utils';
import CustomAttribute from './CustomAttribute';
import ActionListboxField from '@/components/shared/ActionListboxField';

interface PersonalInformationProps {
  worker: User;
}

const RESIDENCY_STATUS_ATTRIBUTE_REFS = [
  'residencyStatusWithMandatoryValidVisa',
  'residencyStatus',
] as const;

const PersonalInformation: React.FC<PersonalInformationProps> = ({
  worker,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const { fetchWorkerById } = useWorkerStore();

  // Build initial values from all custom attributes
  const initialValues = useMemo(() => {
    const values: Record<string, string> = {};
    worker.customAttributes?.forEach((attr) => {
      if (attr?.ref) {
        values[attr.ref] = attr.value || '';
      }
    });
    EXPLICIT_CUSTOM_ATTRIBUTE_REFS.forEach((ref) => {
      values[ref] = getCustomAttribute(worker, ref) || '';
    });
    return values;
  }, [worker]);

  const userCustomAttributeRefs = useMemo(
    () =>
      new Set(
        worker.customAttributes
          ?.map((attribute) => attribute?.ref)
          .filter((ref): ref is string => Boolean(ref)) ?? [],
      ),
    [worker.customAttributes],
  );

  /** Explicit fields not yet on the user (always shown with a dedicated label). */
  const explicitAttributeRefs = useMemo(() => {
    // TODO: Remove this once we have a proper custom attribute configuration from the onboarding, now just make it available for locumco
    if (worker.tenantId !== '82bcc8ee-fafc-4d35-96f1-fd4d19084e6a') {
      return [];
    }
    return EXPLICIT_CUSTOM_ATTRIBUTE_REFS.filter(
      (ref) => !userCustomAttributeRefs.has(ref),
    );
  }, [userCustomAttributeRefs, worker.tenantId]);

  // Dynamic custom attributes; explicit refs already on the user render here instead.
  const attributeRefs = useMemo(() => {
    return (
      worker.customAttributes
        ?.filter(
          (attr) =>
            attr?.ref &&
            !HIDDEN_CUSTOM_ATTRIBUTE_REFS.includes(attr.ref) &&
            (!EXPLICIT_CUSTOM_ATTRIBUTE_REFS.includes(attr.ref) ||
              userCustomAttributeRefs.has(attr.ref)),
        )
        .map((attr) => attr!.ref!) || []
    );
  }, [worker.customAttributes, userCustomAttributeRefs]);

  const handleSave = async (values: Record<string, string>) => {
    try {
      // Build custom attributes array from form values
      const customAttributes = Object.entries(values)
        .filter(([, value]) => value !== undefined && value !== '')
        .map(([ref, value]) => ({ ref, value }));

      const userInput: UserInput = {
        customAttributes,
      };

      await userActions.updateUser(worker.id, userInput);
      // Refresh worker data
      await fetchWorkerById(worker.id);
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating personal information:', error);
    }
  };

  const renderAttributeInputField = (
    ref: string,
    values: Record<string, string>,
    setFieldValue: (field: string, value: string) => void,
  ) => {
    if (
      RESIDENCY_STATUS_ATTRIBUTE_REFS.includes(
        ref as (typeof RESIDENCY_STATUS_ATTRIBUTE_REFS)[number],
      )
    ) {
      return (
        <ActionListboxField
          id={ref}
          value={values[ref] || ''}
          onChange={(value) => setFieldValue(ref, value)}
          options={[...RESIDENCY_STATUS_OPTIONS]}
          placeholder="Select residency status"
          noneOptionLabel="Not specified"
        />
      );
    }

    return (
      <Field
        id={ref}
        name={ref}
        type="text"
        className={actionFieldInputBlockClassName}
      />
    );
  };

  return (
    <EditableSection
      title="Personal Information"
      isEditing={isEditing}
      onEdit={() => setIsEditing(true)}
      onCancel={() => setIsEditing(false)}
      showSaveButton={false}
      collapsible
    >
      {!isEditing ? (
        <div className="grid grid-cols-1 gap-x-4 gap-y-6 px-2 sm:grid-cols-2 sm:gap-x-6 sm:gap-y-6 sm:px-4 lg:grid-cols-3 lg:gap-x-6 lg:gap-y-6 lg:px-0">
          {attributeRefs.map((ref) => (
            <CustomAttribute
              key={ref}
              attributeRef={ref}
              label={
                EXPLICIT_CUSTOM_ATTRIBUTE_REFS.includes(ref)
                  ? getExplicitCustomAttributeLabel(ref)
                  : undefined
              }
              attr={worker.customAttributes?.find((a) => a?.ref === ref)}
            />
          ))}
          {explicitAttributeRefs.map((ref) => (
            <CustomAttribute
              key={ref}
              attributeRef={ref}
              label={getExplicitCustomAttributeLabel(ref)}
              attr={worker.customAttributes?.find(
                (attribute) => attribute?.ref === ref,
              )}
            />
          ))}
        </div>
      ) : (
        <Formik
          initialValues={initialValues}
          onSubmit={handleSave}
          enableReinitialize
        >
          {({ handleSubmit, values, setFieldValue }) => (
            <Form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 gap-x-4 gap-y-6 px-2 sm:grid-cols-2 sm:gap-x-6 sm:gap-y-6 sm:px-4 lg:grid-cols-3 lg:gap-x-6 lg:gap-y-6 lg:px-0">
                {attributeRefs.map((ref) => (
                  <div key={ref} className="min-w-0">
                    <label
                      htmlFor={ref}
                      className="block text-sm font-medium text-gray-500"
                    >
                      {EXPLICIT_CUSTOM_ATTRIBUTE_REFS.includes(ref)
                        ? getExplicitCustomAttributeLabel(ref)
                        : formatLabel(ref)}
                    </label>
                    {renderAttributeInputField(ref, values, setFieldValue)}
                  </div>
                ))}
                {explicitAttributeRefs.map((ref) => (
                  <div key={ref} className="min-w-0">
                    <label
                      htmlFor={ref}
                      className="block text-sm font-medium text-gray-500"
                    >
                      {getExplicitCustomAttributeLabel(ref)}
                    </label>
                    {renderAttributeInputField(ref, values, setFieldValue)}
                  </div>
                ))}
              </div>
              <div className="mt-6 flex flex-col-reverse gap-3 px-2 sm:flex-row sm:justify-end sm:px-0">
                <button
                  type="button"
                  onClick={() => setIsEditing(false)}
                  className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`rounded-md px-4 py-2 text-sm font-medium ${actionPrimaryButtonClassName}`}
                >
                  Save
                </button>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </EditableSection>
  );
};

export default PersonalInformation;
